import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceDot, ReferenceLine } from 'recharts';
import api from '../services/api';
import relancesApi from '../services/suivi-revisions';
import permissionsApi from '../services/permissions';
import { Bascule, BoutonPanneau, Rubrique, ChoixPastilles, Pastilles, IconeFiltres, IconeCalendrier } from './shared/FiltresBarre';
import { isFixedDomTomNumber } from '../services/sms';
import './RelancesAtelier.css';

// Nettoie un message pour la passerelle SMS (pas d'accents ni caractères exotiques).
function stripAccents(str) {
  return str
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[œŒ]/g, m => m === 'œ' ? 'oe' : 'OE')
    .replace(/[æÆ]/g, m => m === 'æ' ? 'ae' : 'AE')
    .replace(/[ç]/g, 'c').replace(/[Ç]/g, 'C')
    // { } pour les variables ({{lien}}…), = & _ % # comme le service SMS : les
    // retirer à la frappe empêchait d'écrire {{lien}} (2026-10-23).
    .replace(/[^a-zA-Z0-9 .,;:!?'"()\-\n/€@+=&_%#{}]/g, '');
}

// ── Formatage ────────────────────────────────────────────────────────────────
const nf = new Intl.NumberFormat('fr-FR');
function fmtKm(n) { return (n || n === 0) ? `${nf.format(n)} km` : '—'; }
function fmtDate(s) {
  if (!s) return '—';
  const iso = String(s).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

const CONTACT_CLASS = {
  a_contacter: 'status-warning', contacte: 'status-success', pris_rdv: 'status-info', ne_pas_contacter: 'status-muted',
  non_renseigne: 'status-muted',
};

const ECHEANCE_FILTERS = [
  { v: '', label: 'Toutes échéances' },
  { v: 'depassee', label: 'Dépassées' },
  { v: '30j', label: 'Sous 30 jours' },
  { v: '60j', label: 'Sous 60 jours' },
  { v: 'avenir', label: 'À venir' },
];
const CONTACT_FILTERS = [
  { v: '', label: 'Tous statuts contact' },
  { v: 'a_contacter', label: 'À contacter' },
  { v: 'contacte', label: 'Contactés' },
  { v: 'pris_rdv', label: 'RDV pris' },
  { v: 'ne_pas_contacter', label: 'Ne pas contacter' },
  { v: 'non_renseigne', label: 'Non renseigné' },
];
const PASSAGES_FILTERS = [
  { v: '', label: 'Tout nb de passages' },
  { v: '1', label: '1 seul passage' },
  { v: '2_5', label: '2 à 5 passages' },
  { v: '6_plus', label: '6 passages et plus' },
];
const PAGE_SIZE = 50;
const libDe = (L, v) => (L.find(o => o.v === v) || {}).label || v;

// Périodes de vente du VN (2026-10-23) : raccourcis → bornes de date.
const PERIODES_VENTE = [
  { value: 'lt6m',   label: 'Moins de 6 mois' },
  { value: '6_12m',  label: '6 à 12 mois' },
  { value: '1_2a',   label: '1 à 2 ans' },
  { value: '2_3a',   label: '2 à 3 ans' },
  { value: 'gt3a',   label: 'Plus de 3 ans' },
];
function bornesVente(p) {
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const recul = (mois) => { const d = new Date(); d.setMonth(d.getMonth() - mois); return iso(d); };
  switch (p) {
    case 'lt6m':  return { du: recul(6), au: '' };
    case '6_12m': return { du: recul(12), au: recul(6) };
    case '1_2a':  return { du: recul(24), au: recul(12) };
    case '2_3a':  return { du: recul(36), au: recul(24) };
    case 'gt3a':  return { du: '', au: recul(36) };
    default:      return { du: '', au: '' };
  }
}
const fmtJourIso = (d) => (d ? d.split('-').reverse().join('/') : '…');

// Dernier statut atelier connu pour la plaque (Planning) : « Restitué le
// 28/08/2026 », « En intervention depuis le 12/10/2026 » ; « ~ » si la date
// est approchée (statut posé avant l'horodatage des statuts).
function AtelierStatut({ v }) {
  if (!v.atelier_statut) return null;
  const ponctuel = /(restitué|facturé|terminée?)\s*$/i.test(v.atelier_statut);
  const d = v.atelier_depuis ? new Date(v.atelier_depuis) : null;
  const date = d && !Number.isNaN(d.getTime()) ? `${ponctuel ? ' le' : ' depuis le'} ${v.atelier_depuis_estime ? '~' : ''}${d.toLocaleDateString('fr-FR')}` : '';
  return (
    <span className="ra-atelier" style={{ background: v.atelier_couleur || 'var(--bg-muted)', color: v.atelier_text_color || 'var(--text-primary)' }}
      title="Dernier statut atelier connu pour cette plaque (Planning Atelier)">
      {v.atelier_statut}<span className="ra-atelier-date">{date}</span>
    </span>
  );
}

// Statut de contact (modifiable) suivi de la date du dernier changement :
// « RDV pris le 12/10/2026 » remplace la colonne « Statut modifié le ».
function StatutContact({ v, onChange }) {
  const valeur = v.contact_statut || 'non_renseigne';
  const libelle = CONTACT_FILTERS.find(o => o.v === valeur)?.label || 'Non renseigné';
  // Pastille unique (statut + date) ; le <select> invisible la recouvre pour rester cliquable.
  return (
    <span className={`ra-statut-pill ${CONTACT_CLASS[v.contact_statut] || 'status-muted'}`} onClick={e => e.stopPropagation()}>
      <span>{libelle}</span>
      {v.contact_statut && v.contacte_le && <span className="ra-statut-date">le {new Date(v.contacte_le).toLocaleDateString('fr-FR')}</span>}
      <select className="ra-statut-pill-select" aria-label="Statut de contact" value={valeur} onChange={e => onChange(v, e.target.value)}>
        {CONTACT_FILTERS.filter(o => o.v).map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </span>
  );
}

// Normalisation d'immatriculation — même convention que `inventaire_parking.identifiant`
// (Gestion Parking) : majuscules, sans espaces ni tirets.
function normImmat(s) { return (s || '').replace(/[-\s]/g, '').toUpperCase(); }

// Badge d'échéance basé sur les jours restants (calculés serveur).
function echeanceBadge(jours, date) {
  if (jours === null || jours === undefined) return { cls: 'status-muted', txt: '—' };
  if (jours < 0) return { cls: 'status-error', txt: `-${-jours}j` };
  if (jours <= 30) return { cls: 'status-warning', txt: `J‑${jours}` };
  if (jours <= 60) return { cls: 'status-info', txt: `J‑${jours}` };
  return { cls: 'status-muted', txt: fmtDate(date) };
}

const ANCIENNETE_FILTERS = [
  { v: '', label: 'Toute ancienneté' },
  { v: '12m_plus', label: '> 12 mois' },
  { v: '6_12m', label: '6 à 12 mois' },
  { v: 'recent', label: '< 6 mois' },
];
function fmtSince(j) {
  if (j === null || j === undefined) return '—';
  if (j >= 365) { const a = Math.floor(j / 365); return `il y a ${a} an${a > 1 ? 's' : ''}`; }
  if (j >= 60) return `il y a ${Math.round(j / 30)} mois`;
  return `il y a ${j} j`;
}
function ancienneteBadge(j) {
  if (j === null || j === undefined) return { cls: 'status-muted', txt: '—' };
  if (j >= 365) return { cls: 'status-error', txt: fmtSince(j) };
  if (j >= 180) return { cls: 'status-warning', txt: fmtSince(j) };
  return { cls: 'status-info', txt: fmtSince(j) };
}

// En-tête de colonne triable (clic = trie ; re-clic = inverse le sens).
// ─── Consentement (brique A97 RGPD, loi du 11/08/2026) ────────────────────────
// c = { actif, autorise, etat, motif, valide_jusqu_au } tel que renvoyé par le
// backend. Une concession sans opt-in reçoit { actif: false } : rien n'est affiché.
const CONSENT_MOTIFS = {
  consentement: 'Consentement recueilli',
  contrat_en_cours: 'Contrat en cours (exception)',
  client_existant: 'Client existant (exception SMS / e-mail)',
  refus: 'Refus enregistré',
  retrait: 'Consentement retiré (opposition)',
  inconnu: 'Aucun consentement connu',
  expire: 'Consentement expiré',
  sans_telephone: 'Aucun numéro de téléphone',
  brique_indisponible: 'Brique RGPD injoignable : prospection refusée (sécurité)',
  non_configure: 'Brique RGPD non configurée sur ce serveur',
  identifiant_invalide: 'Numéro invalide',
};
function consentInfo(c) {
  if (!c || !c.actif) return null;
  if (c.autorise) {
    const jusqu = c.valide_jusqu_au ? ` jusqu'au ${fmtDate(c.valide_jusqu_au)}` : '';
    return { cls: 'status-success', txt: '✅', label: `${CONSENT_MOTIFS[c.motif] || 'Autorisé'}${jusqu}` };
  }
  if (c.motif === 'refus' || c.motif === 'retrait') return { cls: 'status-error', txt: '❌', label: CONSENT_MOTIFS[c.motif] };
  if (c.motif === 'brique_indisponible' || c.motif === 'non_configure') return { cls: 'status-warning', txt: '⚠️', label: CONSENT_MOTIFS[c.motif] };
  return { cls: 'status-muted', txt: '∅', label: CONSENT_MOTIFS[c.motif] || 'Non autorisé' };
}
function ConsentBadge({ c }) {
  const i = consentInfo(c);
  if (!i) return <span className="muted">—</span>;
  return <span className={`ra-consent-badge ${i.cls}`} title={i.label}>{i.txt}</span>;
}

function SortableTh({ label, field, sort, dir, onSort, className }) {
  const active = sort === field;
  return (
    <th className={`ra-th-sortable ${className || ''}`} onClick={() => onSort(field)}>
      {label}
      <span className={`ra-sort-arrow ${active ? 'active' : ''}`}>{active ? (dir === 'desc' ? '▼' : '▲') : '↕'}</span>
    </th>
  );
}

// Aperçu {{var}} côté client (miroir de services/relance-rdv.rendre côté back).
function rendreApercu(modele, { concession = '', immat = '', lien = '' }) {
  const val = { prenom: '', concession, immat: immat ? ` ${immat}` : '', lien };
  return String(modele || '')
    .replace(/\{\{(\w+)\}\}/g, (_, k) => (k in val ? val[k] : `{{${k}}}`))
    .replace(/ {2,}/g, ' ').trim();
}

// Modèle du SMS de relance, édité en un seul endroit (2026-10-23) : bouton
// « ✉️ Message SMS » de l'en-tête. Global (toutes concessions), super-admin.
const VARIABLES_SMS = [
  { cle: '{{immat}}', aide: 'plaque du véhicule' },
  { cle: '{{lien}}', aide: 'lien de prise de RDV (obligatoire)' },
  { cle: '{{concession}}', aide: 'nom de la concession' },
];
function ModeleSmsModal({ onClose }) {
  const [modele, setModele] = useState('');
  const [defaut, setDefaut] = useState('');
  const [enregistre, setEnregistre] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const zone = useRef(null);
  useEffect(() => {
    relancesApi.getRelanceModele()
      .then(({ data }) => { setModele(data.modele || data.defaut || ''); setDefaut(data.defaut || ''); })
      .catch(() => setErreur('Modèle illisible'));
  }, []);
  const inserer = (v) => {
    const el = zone.current;
    const debut = el ? el.selectionStart : modele.length;
    const fin = el ? el.selectionEnd : modele.length;
    const suivant = modele.slice(0, debut) + v + modele.slice(fin);
    setModele(suivant); setEnregistre(false);
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = debut + v.length; } }, 0);
  };
  // Aperçu avec un lien de la longueur réelle (~40 caractères) pour compter juste.
  const apercu = rendreApercu(modele, { concession: 'BLUE AUTOMOBILES', immat: 'GV-293-AA', lien: 'https://rdv.autodom97.fr/?token=abcd123456' });
  const lienManquant = !/\{\{lien\}\}/.test(modele);
  const nb = apercu.length;
  const enregistrer = async () => {
    setErreur(''); setEnCours(true);
    try { await relancesApi.setRelanceModele(modele.trim()); setEnregistre(true); }
    catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setEnCours(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>✉️ Message SMS de relance</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <label className="ra-field">Texte (le même pour toutes les concessions)
            <textarea ref={zone} rows={4} value={modele} onChange={e => { setModele(stripAccents(e.target.value)); setEnregistre(false); }} />
          </label>
          <div className="ra-sms-vars">
            {VARIABLES_SMS.map(v => (
              <button key={v.cle} type="button" className="ra-sms-var" onClick={() => inserer(v.cle)} title={`Insérer : ${v.aide}`}>{v.cle}</button>
            ))}
            <span className="ra-sms-count" style={{ color: nb > 160 ? 'var(--color-error)' : 'var(--text-muted)' }}>
              {nb} / 160 caractères{nb > 160 ? ' : le SMS partira en 2 messages' : ''}
            </span>
          </div>
          <div className="ra-sms-target" style={{ marginTop: 6 }}>
            <div className="ra-sub" style={{ fontWeight: 600 }}>Aperçu (exemple)</div>
            <div className="ra-sub">{apercu || '—'}</div>
          </div>
          <div className="ra-sub" style={{ color: 'var(--text-muted)' }}>Accents retirés automatiquement : ils font passer le SMS en deux messages.</div>
          {lienManquant && <div className="ra-error">⚠️ Le message doit contenir {'{{lien}}'} : c'est le lien de prise de RDV.</div>}
          {erreur && <div className="ra-error">⚠️ {erreur}</div>}
          {enregistre && <div className="ra-info">✅ Modèle enregistré : il s'applique aux prochains envois.</div>}
        </div>
        <div className="modal-footer">
          {defaut && <button className="btn-secondary" onClick={() => { setModele(defaut); setEnregistre(false); }}>Texte par défaut</button>}
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn-primary" onClick={enregistrer} disabled={enCours || !modele.trim() || lienManquant}>{enCours ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}

// Modale « Proposer un RDV » (#2b) : envoie un SMS avec un lien de prise de RDV
// en ligne pré-rempli, porté par un token. Le modèle est GLOBAL (signature
// {{concession}}, lien {{lien}} par concession) ; le lien réel est généré par le
// serveur à l'envoi. Le super-admin peut enregistrer le modèle global ici même.
function SmsModal({ target, user, onClose, onSent }) {
  const [telephone, setTelephone] = useState(target.telephone || '');
  const [modele, setModele] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const phoneBlocked = isFixedDomTomNumber(telephone);

  useEffect(() => {
    relancesApi.getRelanceModele()
      .then(({ data }) => setModele(data.modele || data.defaut || ''))
      .catch(() => setModele(''));
  }, []);

  const apercu = rendreApercu(modele, {
    concession: target.concession_nom || '',
    immat: target.immat || '',
    lien: 'https://rdv…/?token=…',
  });
  const lienManquant = !/\{\{lien\}\}/.test(modele);

  async function handleSend() {
    if (!telephone.trim() || phoneBlocked || !modele.trim() || lienManquant) return;
    if (!window.confirm(`Envoyer la proposition de RDV ?\n\nDestinataire : ${telephone.trim()}\n\n${apercu}`)) return;
    setError(''); setSending(true);
    try {
      await relancesApi.envoyerRelanceRdv(target.chassis, { telephone: telephone.trim() });
      setSuccess(true);
      if (onSent) onSent();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  }


  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>📅 Proposer un RDV</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {phoneBlocked && <div className="ra-error" style={{ marginBottom: 14 }}>⚠️ Numéro fixe DOM‑TOM (0590 / 0596 / 0594) — envoi SMS interdit sur ces lignes.</div>}
          <div className="ra-sms-target">
            <div className="ra-immat">{target.nom || '—'}</div>
            <div className="ra-sub">{[target.immat, target.concession_nom].filter(Boolean).join(' · ')}</div>
          </div>
          <label className="ra-field">Numéro
            <input value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="0690xxxxxx" />
          </label>
          <div className="ra-sms-target" style={{ marginTop: 6 }}>
            <div className="ra-sub" style={{ fontWeight: 600 }}>Message envoyé</div>
            <div className="ra-sub">{apercu || '—'}</div>
            <div className="ra-sub" style={{ marginTop: 6, color: 'var(--text-muted)' }}>
              Le texte est le même pour toutes les relances : il se modifie avec le bouton « ✉️ Message SMS » en haut de la page.
            </div>
          </div>
          {lienManquant && <div className="ra-error">⚠️ Le message doit contenir {'{{lien}}'}.</div>}
          {error && <div className="ra-error">⚠️ {error}</div>}
          {success && <div className="ra-info">✅ Proposition de RDV envoyée.</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn-primary" onClick={handleSend}
            disabled={sending || !telephone.trim() || phoneBlocked || !modele.trim() || lienManquant}>
            {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Graphique kilométrique (historique des passages atelier) ──────────────────
//
// Axe X = temps (échelle trimestrielle), axe Y = km relevé à chaque passage.
// Objectif : repérer à l'œil si le km estimé (calculé côté serveur par régression)
// colle à la tendance réelle, sans avoir à relire chaque ligne de l'historique.

// Débuts de trimestre (UTC) entre deux timestamps — ticks de l'axe X.
function quarterTicks(minTs, maxTs) {
  const ticks = [];
  const start = new Date(minTs);
  let y = start.getUTCFullYear();
  let q = Math.floor(start.getUTCMonth() / 3);
  let cur = Date.UTC(y, q * 3, 1);
  while (cur <= maxTs) {
    ticks.push(cur);
    q++; if (q > 3) { q = 0; y++; }
    cur = Date.UTC(y, q * 3, 1);
  }
  return ticks;
}
function fmtQuarter(ts) {
  const d = new Date(ts);
  return `T${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
}

// Anniversaires tous les 12 mois depuis la date de vente — rappel de la règle
// « révision tous les 15 000 km OU tous les 12 mois », le 2e critère n'étant pas
// toujours visible sur la seule courbe des passages.
function anniversaryTicks(saleTs, maxTs) {
  if (!saleTs) return [];
  const sale = new Date(saleTs);
  const ticks = [];
  for (let k = 1; k <= 30; k++) {
    const d = Date.UTC(sale.getUTCFullYear(), sale.getUTCMonth() + 12 * k, sale.getUTCDate());
    if (d > maxTs) break;
    ticks.push(d);
  }
  return ticks;
}

function KmChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="ra-chart-tooltip">
      <div className="tooltip-title">{fmtDate(p.dateIso)}</div>
      <div className="tooltip-row">{fmtKm(p.km)}</div>
      {p.operation && <div className="tooltip-row muted">{p.operation}{p.num_facture ? ` · ${p.num_facture}` : ''}</div>}
    </div>
  );
}

// Point de départ (0 km à la date de vente) — permet de tracer une tendance dès
// UN SEUL passage réel : vente (0 km) → 1er passage (date, km) donne déjà un rythme
// km/temps exploitable, sans attendre un 2e passage.
function KmChartDot(props) {
  const { cx, cy, payload } = props;
  if (payload.synthetic) {
    return <circle cx={cx} cy={cy} r={4} fill="var(--bg-surface)" stroke="var(--color-accent)" strokeWidth={2} strokeDasharray="2 2" />;
  }
  return <circle cx={cx} cy={cy} r={4} fill="var(--color-accent)" />;
}

function KmChart({ passages, kmEstimeActuel, dateVenteVn }) {
  const real = (passages || [])
    .filter(p => p.date_facture && (p.kilometrage || p.kilometrage === 0))
    .map(p => ({ x: new Date(p.date_facture).getTime(), km: p.kilometrage, dateIso: p.date_facture, operation: p.operation, num_facture: p.num_facture, synthetic: false }))
    .sort((a, b) => a.x - b.x);

  const saleTs = dateVenteVn ? new Date(dateVenteVn).getTime() : null;
  const points = [...real];
  if (saleTs && (points.length === 0 || saleTs < points[0].x)) {
    points.unshift({ x: saleTs, km: 0, dateIso: dateVenteVn, operation: 'Vente — point de départ (0 km, estimé)', num_facture: null, synthetic: true });
  }

  if (points.length < 2) {
    return (
      <div className="ra-chart-card ra-chart-empty">
        Pas assez de données (passage avec kilométrage et/ou date de vente) pour tracer une tendance.
      </div>
    );
  }

  const now = Date.now();
  const minX = points[0].x;
  const maxX = Math.max(now, points[points.length - 1].x);
  const ticks = quarterTicks(minX, maxX);
  const anniversaries = anniversaryTicks(saleTs, maxX);

  return (
    <div className="ra-chart-card">
      <div className="ra-chart-title">Historique kilométrique (passages atelier)</div>
      {anniversaries.length > 0 && (
        <div className="ra-chart-legend">┊ Anniversaire (tous les 12 mois depuis la vente — révision due tous les 15 000 km OU 12 mois)</div>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={points} margin={{ top: 10, right: 24, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
          <XAxis dataKey="x" type="number" domain={[minX, maxX]} ticks={ticks} tickFormatter={fmtQuarter} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
          <YAxis dataKey="km" tickFormatter={v => nf.format(v)} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} width={64} />
          <Tooltip content={<KmChartTooltip />} />
          {anniversaries.map(ts => (
            <ReferenceLine key={ts} x={ts} stroke="var(--color-warning)" strokeDasharray="4 4" strokeWidth={1.5} ifOverflow="extendDomain" />
          ))}
          <Line type="linear" dataKey="km" stroke="var(--color-accent)" strokeWidth={2} dot={<KmChartDot />} activeDot={{ r: 6 }} />
          {kmEstimeActuel != null && (
            <ReferenceDot
              x={now} y={kmEstimeActuel} r={6} fill="var(--color-warning)" stroke="var(--bg-surface)" strokeWidth={2}
              ifOverflow="extendDomain"
              label={{ value: `≈ ${fmtKm(kmEstimeActuel)} auj.`, position: 'top', fill: 'var(--color-warning-dark)', fontSize: 11, fontWeight: 600 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Modale pop-up unifiée « fiche véhicule » ───────────────────────────────────
//
// Remplace le tiroir latéral + la petite modale de suivi de contact. Communes aux
// 2 onglets : navigation ◀/▶ dans la liste courante, coordonnées, statut/canal/note
// éditables en direct (sauvegarde immédiate, pas de bouton « Mettre à jour »).
// Historique + graphique km réservés au mode « contacter » (passages disponibles).
function VehiculeDetailModal({ detail, detailLoading, navList, onClose, onNav, onStatutChange, onCanalChange, onNoteSave, onOpenSms, onDemanderConsentement, consentSending, consentMsg }) {
  const v = detail.vehicule;
  const isJamais = detail.mode === 'jamais';
  const hasChassis = !!v?.chassis;
  const [note, setNote] = useState(v?.note || '');
  // Brique RGPD : { actif, tel, sms } ; l'appel commercial est bloqué à l'écran
  // (numéros non cliquables) tant que la finalité téléphone n'est pas autorisée.
  const consent = v?.consentement;
  const appelBloque = !!(consent?.actif && consent.tel && !consent.tel.autorise && consent.tel.motif !== 'sans_telephone');

  const navKey = (r) => r?.chassis || r?.id;
  const idx = navList.findIndex(r => navKey(r) === navKey(v));
  const goPrev = () => { if (idx > 0) onNav(navList[idx - 1]); };
  const goNext = () => { if (idx >= 0 && idx < navList.length - 1) onNav(navList[idx + 1]); };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <button className="ra-modal-nav-btn" onClick={goPrev} disabled={idx <= 0} title="Dossier précédent">◀</button>
          <h2>{detailLoading ? 'Chargement…' : (v?.immatriculation || v?.chassis || '—')}</h2>
          {navList.length > 0 && <span className="ra-modal-nav-counter">{idx + 1} / {navList.length}</span>}
          <button className="ra-modal-nav-btn" onClick={goNext} disabled={idx < 0 || idx >= navList.length - 1} title="Dossier suivant">▶</button>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {detailLoading ? <div className="ra-empty">Chargement…</div> : v && (
            <>
              <div className="ra-veh-title">{[v.marque, v.modele].filter(Boolean).join(' ')}</div>
              <div className="ra-sub">{v.nom_client} · {v.concession_nom || v.code_societe}</div>

              <div className="ra-facts">
                <div><span className="ra-fact-label">Vendu le</span>{fmtDate(v.date_vente_vn)}</div>
                {isJamais ? (
                  <div><span className="ra-fact-label">Ancienneté</span>{fmtSince(v.jours_depuis_vente)}</div>
                ) : (
                  <>
                    <div><span className="ra-fact-label">Dernier passage</span>{fmtDate(v.dernier_passage)}</div>
                    <div><span className="ra-fact-label">Dernier km</span>{fmtKm(v.dernier_km)}</div>
                    <div><span className="ra-fact-label">Km estimé</span>{fmtKm(v.km_estime_actuel)}</div>
                    <div><span className="ra-fact-label">Échéance</span>{fmtDate(v.prochaine_echeance)}</div>
                    <div><span className="ra-fact-label">Passages</span>{v.nb_passages}</div>
                  </>
                )}
              </div>

              <div className="ra-contact-block">
                {appelBloque ? (
                  <>
                    {v.tel1_mobile && <span className="ra-tel-bloque" title="Appel commercial interdit sans consentement (loi du 11 août 2026)">📞 {v.tel1_mobile}</span>}
                    {v.tel2_domicile && <span className="ra-tel-bloque" title="Appel commercial interdit sans consentement (loi du 11 août 2026)">☎️ {v.tel2_domicile}</span>}
                  </>
                ) : (
                  <>
                    {v.tel1_mobile && <a href={`tel:${v.tel1_mobile}`}>📞 {v.tel1_mobile}</a>}
                    {v.tel2_domicile && <a href={`tel:${v.tel2_domicile}`}>☎️ {v.tel2_domicile}</a>}
                  </>
                )}
                {v.email && <a href={`mailto:${v.email}`}>✉️ {v.email}</a>}
                {!v.tel1_mobile && !v.tel2_domicile && !v.email && <span className="muted">aucun contact</span>}
                {(v.tel1_mobile || v.tel2_domicile) && (
                  <button className="ra-sms-btn" title="Proposer un RDV par SMS" onClick={() => onOpenSms(v)}>📅</button>
                )}
              </div>

              {consent?.actif && (
                <div className={`ra-consent-block ${appelBloque ? 'bloque' : ''}`}>
                  <div className="ra-consent-title">🛡️ Consentement (loi du 11 août 2026)</div>
                  <div className="ra-consent-lines">
                    <div><ConsentBadge c={consent.tel} /> Appel commercial : {consentInfo(consent.tel)?.label || '—'}</div>
                    <div><ConsentBadge c={consent.sms} /> SMS commercial : {consentInfo(consent.sms)?.label || '—'}</div>
                  </div>
                  {appelBloque && <div className="ra-consent-warn">Appel commercial interdit tant que le client n'a pas donné son accord (démarchage téléphonique : opt-in préalable, valable 12 mois). Un SMS de service ou un appel à la demande du client restent possibles.</div>}
                  {(v.tel1_mobile || v.tel2_domicile) && consent.tel?.motif !== 'refus' && consent.tel?.motif !== 'retrait' && (
                    <button className="btn-secondary ra-consent-btn" disabled={consentSending} onClick={onDemanderConsentement}>
                      {consentSending ? 'Envoi…' : '📨 Demander le consentement par SMS'}
                    </button>
                  )}
                  {consentMsg && <div className="ra-sub">{consentMsg}</div>}
                </div>
              )}

              {!hasChassis ? (
                <div className="ra-note">Suivi de contact indisponible (aucun châssis connu pour ce véhicule).</div>
              ) : (
                <div className="ra-edit-block">
                  <div className="ra-edit-row">
                    <label className="ra-field">Statut
                      <select className="ra-inline-select" value={v.contact_statut || 'non_renseigne'} onChange={e => onStatutChange(e.target.value)}>
                        <option value="non_renseigne">Non renseigné</option>
                        <option value="a_contacter">À contacter</option>
                        <option value="contacte">Contacté</option>
                        <option value="pris_rdv">RDV pris</option>
                        <option value="ne_pas_contacter">Ne pas contacter</option>
                      </select>
                    </label>
                    <label className="ra-field">Canal
                      <select className="ra-inline-select" value={v.canal || 'tel'} onChange={e => onCanalChange(e.target.value)}>
                        <option value="tel">Téléphone</option>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="autre">Autre</option>
                      </select>
                    </label>
                  </div>
                  <label className="ra-field">Note
                    <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                      onBlur={() => { if (note !== (v.note || '')) onNoteSave(note); }} placeholder="Commentaire (optionnel)…" />
                  </label>
                  {v.contacte_le && <div className="ra-sub">Dernier suivi par {v.contacte_prenom || ''} {v.contacte_nom || ''} le {fmtDate(v.contacte_le)}</div>}
                </div>
              )}

              {isJamais ? (
                <div className="ra-note">🚙 Véhicule neuf vendu, jamais venu en atelier — aucun historique de passage à afficher.</div>
              ) : (
                <>
                  <KmChart passages={detail.passages || []} kmEstimeActuel={v.km_estime_actuel} dateVenteVn={v.date_vente_vn} />
                  <h4 className="ra-timeline-title">Historique des passages ({(detail.passages || []).length})</h4>
                  <div className="ra-timeline">
                    {(detail.passages || []).map((p, i) => (
                      <div className="ra-tl-item" key={i}>
                        <div className="ra-tl-date">{fmtDate(p.date_facture)}</div>
                        <div className="ra-tl-body">
                          <div className="ra-tl-op">{p.operation || '—'}</div>
                          <div className="ra-sub">{fmtKm(p.kilometrage)}{p.num_facture ? ` · ${p.num_facture}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Exclusions (2026-10-23) ───────────────────────────────────────────
// Clients à ne plus relancer, par code client et par concession. Les codes
// listés ici sortent des onglets À contacter et Jamais venus (et des compteurs).
function ExclusionsRelances({ user, privileged, peutModifier, onChange }) {
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('');
  // Concession : celle du menu (2026-10-26) — filtre la liste et reçoit les ajouts.
  const [concessionId] = useState(user?.concession_id ? String(user.concession_id) : '');
  const [codes, setCodes] = useState('');
  const [motif, setMotif] = useState('');
  const [message, setMessage] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    relancesApi.getExclusions(privileged && concessionId ? { concession_id: concessionId } : {})
      .then(r => setListe(r.data.exclusions || []))
      .catch(e => setErreur(e.response?.data?.error || e.message))
      .finally(() => setChargement(false));
  }, [privileged, concessionId]);
  useEffect(() => { charger(); }, [charger]);

  const ajouter = async () => {
    setErreur(null); setMessage(null);
    try {
      const r = await relancesApi.ajouterExclusions({ concession_id: concessionId || user?.concession_id, codes, motif });
      setMessage(`${r.data.ajoutes} code(s) ajouté(s)${r.data.deja ? ` · ${r.data.deja} déjà présent(s)` : ''}`);
      setCodes(''); setMotif('');
      charger(); onChange && onChange();
    } catch (e) { setErreur(e.response?.data?.error || e.message); }
  };
  const retirer = async (x) => {
    try { await relancesApi.retirerExclusion(x.id); charger(); onChange && onChange(); }
    catch (e) { setErreur(e.response?.data?.error || e.message); }
  };

  const f = filtre.trim().toLowerCase();
  const affichees = f ? liste.filter(x => `${x.code_client} ${x.nom_client || ''} ${x.motif || ''}`.toLowerCase().includes(f)) : liste;

  return (
    <>
      <div className="page-toolbar ra-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher (code, client, motif)…" value={filtre} onChange={e => setFiltre(e.target.value)} />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
      </div>

      {peutModifier && (
        <div className="ra-excl-form">
          <div className="ra-excl-champ">
            <label htmlFor="ra-excl-codes">Codes client à exclure</label>
            <textarea id="ra-excl-codes" rows={3} value={codes} onChange={e => setCodes(e.target.value)}
              placeholder="Un ou plusieurs codes, séparés par un retour à la ligne, un espace ou une virgule (copier-coller d'Excel possible)" />
          </div>
          <div className="ra-excl-champ">
            <label htmlFor="ra-excl-motif">Motif (facultatif)</label>
            <input id="ra-excl-motif" type="text" value={motif} onChange={e => setMotif(e.target.value)} placeholder="Ex. : client professionnel, demande du client…" />
            <div className="ra-excl-actions">
              {privileged && !concessionId && <span className="ra-excl-hint">Choisissez d'abord une concession dans le menu.</span>}
              <button type="button" className="btn-primary" disabled={!codes.trim() || (privileged && !concessionId)} onClick={ajouter}>Exclure</button>
            </div>
          </div>
        </div>
      )}

      {message && <div className="ra-info">✅ {message}</div>}
      {erreur && <div className="ra-error">⚠️ {erreur}</div>}
      <div className="ra-count">{chargement ? 'Chargement…' : `${affichees.length} client(s) exclu(s) des relances`}</div>

      <div className="table-container">
        <table className="ra-table">
          <thead>
            <tr>
              <th>Code client</th>
              <th>Client</th>
              {privileged && <th>Concession</th>}
              <th>Motif</th>
              <th>Ajouté par</th>
              <th className="date">Le</th>
              {peutModifier && <th></th>}
            </tr>
          </thead>
          <tbody>
            {affichees.map(x => (
              <tr key={x.id}>
                <td><b>{x.code_client}</b></td>
                <td><div className="ra-client-name" title={x.nom_client || ''}>{x.nom_client || ''}</div></td>
                {privileged && <td>{x.concession_nom}</td>}
                <td>{x.motif || ''}</td>
                <td>{[x.cree_par_prenom, x.cree_par_nom].filter(Boolean).join(' ')}</td>
                <td className="date">{x.created_at ? new Date(x.created_at).toLocaleDateString('fr-FR') : ''}</td>
                {peutModifier && <td><button type="button" className="ra-excl-retirer" onClick={() => retirer(x)} title="Remettre ce client dans les relances">Retirer</button></td>}
              </tr>
            ))}
            {!chargement && affichees.length === 0 && (
              <tr><td colSpan={7} className="ra-empty">Aucun client exclu.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RelancesAtelier({ user, setHeaderAction, editMode }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const privileged = ['super-admin', 'admin'].includes(user?.role);

  const [tab, setTab] = useState('contacter');
  const [modeleSmsOuvert, setModeleSmsOuvert] = useState(false);
  // Période de vente : commune aux onglets À contacter et Jamais venus.
  const [ventePreset, setVentePreset] = useState('');
  const [venteDu, setVenteDu] = useState('');
  const [venteAu, setVenteAu] = useState('');
  const choisirVente = (p) => { const b = bornesVente(p); setVentePreset(p); setVenteDu(b.du); setVenteAu(b.au); };
  // Droit « modifier » de la page : ajouter / retirer des exclusions.
  const [peutModifier, setPeutModifier] = useState(isSuperAdmin);
  useEffect(() => {
    permissionsApi.checkAccess('suivi_revisions')
      .then(r => setPeutModifier(!!r.data?.permissions?.peut_modifier))
      .catch(() => {});
  }, []);
  const [vehicules, setVehicules] = useState([]);
  // Colonne « Consent. tél. » affichée dès qu'une concession de la page a activé la brique RGPD.
  const consentActif = vehicules.some(v => v.consentement && v.consentement.actif);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const [q, setQ] = useState('');
  const [echeance, setEcheance] = useState('');
  const [contact, setContact] = useState('');
  // Concession : celle du menu (2026-10-26), envoyée en concession_id aux API.
  const [concession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [marque, setMarque] = useState('');
  const [marques, setMarques] = useState([]);
  const [nbPassages, setNbPassages] = useState('');
  const [offset, setOffset] = useState(0);
  const [montrerTout, setMontrerTout] = useState(false); // false = masque flottes/cessions + retards anciens
  const [stats, setStats] = useState(null);
  const [sort, setSort] = useState('prochaine_echeance');
  const [sortDir, setSortDir] = useState('asc');
  const [parkingFound, setParkingFound] = useState(new Set()); // immats (normalisées) identifiées dans Gestion Parking

  const [detail, setDetail] = useState(null);     // { vehicule, passages, mode: 'contacter'|'jamais' }
  const [detailLoading, setDetailLoading] = useState(false);
  const navVehiculesRef = useRef([]);              // snapshot de la liste courante au moment de l'ouverture (navigation ◀/▶ stable)
  const [smsModal, setSmsModal] = useState(null);          // { nom, immat, telephone, concession_id, concession_nom }
  const [consentSending, setConsentSending] = useState(false);
  const [consentMsg, setConsentMsg] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [undoAction, setUndoAction] = useState(null);      // { chassis, prevStatut } pour annuler un « contacté »
  const [focusIdx, setFocusIdx] = useState(-1);            // ligne focalisée (navigation clavier, onglet À contacter)

  // Onglet « Jamais revenus » (VN vendus jamais venus en atelier).
  const [jr, setJr] = useState({ vehicules: [], total: 0 });
  const [jrLoading, setJrLoading] = useState(false);
  const [jrQ, setJrQ] = useState('');
  const [jrAnc, setJrAnc] = useState('');
  const [jrContact, setJrContact] = useState('');
  const [jrOffset, setJrOffset] = useState(0);
  const [jrSort, setJrSort] = useState('date_vente_vn');
  const [jrSortDir, setJrSortDir] = useState('asc');

  const qTimer = useRef(null);

  // Bouton « Synchroniser » dans le header.
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({ secondary: [
      ...(isSuperAdmin ? [{ label: '✉️ Message SMS', onClick: () => setModeleSmsOuvert(true) }] : []),
      { label: syncing ? 'Synchronisation…' : '↻ Synchroniser SFTP', onClick: handleSync },
    ] });
    return () => setHeaderAction && setHeaderAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  // Marques (filtre, communes aux 2 onglets).
  useEffect(() => {
    relancesApi.getMarques().then(({ data }) => setMarques(data.marques || [])).catch(() => {});
  }, []);

  // Vérifie en masse (endpoint déjà utilisé par Gestion Parking) quelles immatriculations
  // de la page courante sont identifiées au parking — alimente le picto 🅿️ sur les lignes.
  const checkParking = useCallback((rows) => {
    const immats = (rows || []).map(r => r.immatriculation).filter(Boolean);
    if (!immats.length) return;
    api.post('/parking/check-immat-batch', { immat: immats })
      .then(({ data }) => setParkingFound(prev => new Set([...prev, ...Object.keys(data.found || {})])))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    relancesApi.getVehicules({ q, echeance, contact, concession_id: concession, marque, nb_passages: nbPassages, vente_du: venteDu || undefined, vente_au: venteAu || undefined, exclure_flottes: montrerTout ? 0 : 1, limit: PAGE_SIZE, offset, sort, dir: sortDir })
      .then(({ data }) => { setVehicules(data.vehicules || []); setTotal(data.total || 0); checkParking(data.vehicules); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [q, echeance, contact, concession, marque, nbPassages, venteDu, venteAu, montrerTout, offset, sort, sortDir, checkParking]);

  // Clic sur un en-tête : trie par cette colonne (même colonne re-cliquée = inverse le sens).
  function handleSort(field) {
    if (sort === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(field); setSortDir('asc'); }
    setOffset(0);
  }
  function handleJrSort(field) {
    if (jrSort === field) setJrSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setJrSort(field); setJrSortDir('asc'); }
    setJrOffset(0);
  }

  const loadStats = useCallback(() => {
    relancesApi.getStats({ concession_id: concession, exclure_flottes: montrerTout ? 0 : 1 })
      .then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, [concession, montrerTout]);

  useEffect(() => { if (tab === 'contacter') { load(); loadStats(); } }, [load, loadStats, tab]);
  // Reset pagination quand un filtre change.
  useEffect(() => { setOffset(0); }, [q, echeance, contact, concession, marque, nbPassages, venteDu, venteAu, montrerTout]);

  const loadJr = useCallback(() => {
    setJrLoading(true); setError(null);
    relancesApi.getJamaisRevenus({ q: jrQ, anciennete: jrAnc, contact: jrContact, concession_id: concession, marque, vente_du: venteDu || undefined, vente_au: venteAu || undefined, limit: PAGE_SIZE, offset: jrOffset, sort: jrSort, dir: jrSortDir })
      .then(({ data }) => { setJr({ vehicules: data.vehicules || [], total: data.total || 0 }); checkParking(data.vehicules); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setJrLoading(false));
  }, [jrQ, jrAnc, jrContact, concession, marque, venteDu, venteAu, jrOffset, jrSort, jrSortDir, checkParking]);

  useEffect(() => { if (tab === 'jamais') loadJr(); }, [loadJr, tab]);
  useEffect(() => { setJrOffset(0); }, [jrQ, jrAnc, jrContact, concession, marque, venteDu, venteAu]);

  // Navigation clavier (onglet À contacter) : j/k = déplacer, c = contacté, Entrée = détail.
  useEffect(() => { setFocusIdx(-1); }, [vehicules]);
  useEffect(() => {
    if (tab !== 'contacter') return undefined;
    function onKey(e) {
      if (smsModal || detail) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (!vehicules.length) return;
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx(i => Math.min(vehicules.length - 1, i + 1)); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx(i => Math.max(0, i < 0 ? 0 : i - 1)); }
      else if (e.key === 'c' && focusIdx >= 0) { const v = vehicules[focusIdx]; if (v && (!v.contact_statut || v.contact_statut === 'a_contacter')) quickContacted(v.chassis); }
      else if (e.key === 'Enter' && focusIdx >= 0) { const v = vehicules[focusIdx]; if (v) openDetail(v.chassis, vehicules); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, vehicules, focusIdx, smsModal, detail]);

  function onSearch(e) {
    const val = e.target.value;
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => setQ(val), 350);
  }

  async function handleSync() {
    if (syncing) return;
    setSyncing(true); setSyncMsg(null); setError(null);
    try {
      const { data } = await relancesApi.sync();
      setSyncMsg(`Synchronisé : ${nf.format(data.passages)} passages · ${nf.format(data.vehicules)} véhicules.`);
      load(); loadStats();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setSyncing(false); }
  }

  // Récupère la fiche complète (faits + historique des passages) — onglet À contacter.
  function fetchContacter(chassis) {
    setDetailLoading(true);
    relancesApi.getVehicule(chassis)
      .then(({ data }) => setDetail({ ...data, mode: 'contacter' }))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setDetailLoading(false));
  }
  // Ouverture depuis l'onglet À contacter (fetch complet, historique de passages).
  // On pose un detail « coquille vide » tout de suite pour que la modale (et son
  // spinner) s'affiche immédiatement, sans attendre la réponse du serveur.
  function openDetail(chassis, navList) {
    navVehiculesRef.current = navList || [];
    setDetail({ vehicule: null, passages: null, mode: 'contacter' });
    fetchContacter(chassis);
  }
  // Ouverture depuis l'onglet Jamais venus (pas de passage à charger, la ligne suffit).
  function openDetailJamais(row, navList) {
    navVehiculesRef.current = navList || [];
    setDetail({ vehicule: row, passages: null, mode: 'jamais' });
  }
  // Flèches ◀/▶ de la modale : ré-ouvre sur le dossier voisin selon le mode courant.
  function handleNavDetail(newRow) {
    if (!newRow) return;
    if (detail?.mode === 'jamais') setDetail({ vehicule: newRow, passages: null, mode: 'jamais' });
    else fetchContacter(newRow.chassis);
  }

  // Répercute un patch (statut/canal/note) sur la ligne correspondante dans les 2 listes,
  // pour que le tableau en arrière-plan reste synchro sans recharger toute la page.
  function updateListRow(chassis, patch) {
    setVehicules(prev => prev.map(r => r.chassis === chassis ? { ...r, ...patch } : r));
    setJr(prev => ({ ...prev, vehicules: prev.vehicules.map(r => r.chassis === chassis ? { ...r, ...patch } : r) }));
  }

  // Sauvegarde immédiate (statut / canal / note) depuis la modale — pas de bouton « Enregistrer ».
  async function saveContactField(patch) {
    const v = detail?.vehicule;
    if (!v?.chassis) return;
    const payload = {
      statut: patch.statut ?? v.contact_statut ?? 'a_contacter',
      canal: patch.canal ?? v.canal ?? 'tel',
      note: patch.note ?? v.note ?? '',
    };
    const optimistic = payload.statut === 'non_renseigne'
      ? { contact_statut: null, canal: null, note: null, contacte_le: null, contacte_prenom: '', contacte_nom: '' }
      : {
          contact_statut: payload.statut, canal: payload.canal, note: payload.note,
          contacte_le: payload.statut === 'a_contacter' ? null : new Date().toISOString(),
          contacte_prenom: user?.prenom || '', contacte_nom: user?.nom || '',
        };
    const prevVeh = v;
    setDetail(d => ({ ...d, vehicule: { ...d.vehicule, ...optimistic } }));
    updateListRow(v.chassis, optimistic);
    try {
      await relancesApi.setContact(v.chassis, payload);
    } catch (err) {
      setDetail(d => ({ ...d, vehicule: prevVeh }));
      updateListRow(v.chassis, prevVeh);
      setError(err.response?.data?.error || err.message);
    }
  }

  // Changement de statut directement depuis la liste déroulante du tableau (sans ouvrir la modale).
  async function setRowStatut(v, statut) {
    if (!v?.chassis || statut === (v.contact_statut || 'non_renseigne')) return;
    const payload = { statut, canal: v.canal || 'tel', note: v.note || '' };
    const optimistic = statut === 'non_renseigne'
      ? { contact_statut: null, canal: null, note: null, contacte_le: null, contacte_prenom: '', contacte_nom: '' }
      : {
          contact_statut: payload.statut, canal: payload.canal, note: payload.note,
          contacte_le: payload.statut === 'a_contacter' ? null : new Date().toISOString(),
          contacte_prenom: user?.prenom || '', contacte_nom: user?.nom || '',
        };
    updateListRow(v.chassis, optimistic);
    if (detail?.vehicule?.chassis === v.chassis) setDetail(d => ({ ...d, vehicule: { ...d.vehicule, ...optimistic } }));
    try {
      await relancesApi.setContact(v.chassis, payload);
    } catch (err) {
      updateListRow(v.chassis, v);
      if (detail?.vehicule?.chassis === v.chassis) setDetail(d => ({ ...d, vehicule: v }));
      setError(err.response?.data?.error || err.message);
    }
  }

  function reloadCurrent() { if (tab === 'jamais') loadJr(); else { load(); loadStats(); } }

  // Marquage « contacté » en un clic (canal téléphone), avec possibilité d'annuler.
  async function quickContacted(chassis, prevStatut = 'a_contacter') {
    if (!chassis) return;
    try {
      await relancesApi.setContact(chassis, { statut: 'contacte', canal: 'tel' });
      setUndoAction({ chassis, prevStatut });
      reloadCurrent();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  }
  async function undoLast() {
    if (!undoAction) return;
    try {
      await relancesApi.setContact(undoAction.chassis, { statut: undoAction.prevStatut });
      setUndoAction(null);
      reloadCurrent();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  }

  function toggleSelect(chassis) {
    setSelected(prev => { const n = new Set(prev); n.has(chassis) ? n.delete(chassis) : n.add(chassis); return n; });
  }
  async function deleteSelected() {
    if (!selected.size) return;
    if (!window.confirm(`Supprimer ${selected.size} véhicule(s) du suivi ? (réapparaîtront à la prochaine synchronisation)`)) return;
    try { await relancesApi.remove([...selected]); setSelected(new Set()); load(); }
    catch (err) { setError(err.response?.data?.error || err.message); }
  }

  const canDelete = isSuperAdmin && editMode;
  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="page-container ra-page">
      {/* 1. Onglets */}
      <div className="page-tabs">
        <button className={tab === 'contacter' ? 'active' : ''} onClick={() => setTab('contacter')}>📞 À contacter</button>
        <button className={tab === 'jamais' ? 'active' : ''} onClick={() => setTab('jamais')}>🚙 Jamais venus</button>
        <button className={tab === 'exclusions' ? 'active' : ''} onClick={() => setTab('exclusions')}>🚫 Exclusions</button>
      </div>

      {undoAction && (
        <div className="ra-undo">
          <span>✓ Marqué « contacté ».</span>
          <button className="ra-undo-link" onClick={undoLast}>Annuler</button>
          <button className="ra-undo-x" title="Fermer" onClick={() => setUndoAction(null)}>✕</button>
        </div>
      )}

      {tab === 'contacter' && (
        <>
          {/* 2. Barre de filtres — même modèle qu'OR en cours et Gestion Parking
              (FiltresBarre, 2026-10-23) : recherche, vue relançable / tout, le
              reste dans « Filtres », chaque filtre actif en pastille. */}
          {(() => {
            const actifs = [
              (venteDu || venteAu) && { cle: 'vente', libelle: 'Vendu', valeur: ventePreset ? PERIODES_VENTE.find(x => x.value === ventePreset).label : `${fmtJourIso(venteDu)} → ${fmtJourIso(venteAu)}`, retirer: () => choisirVente('') },
              echeance && { cle: 'echeance', libelle: 'Échéance', valeur: libDe(ECHEANCE_FILTERS, echeance), retirer: () => setEcheance('') },
              contact && { cle: 'contact', libelle: 'Contact', valeur: libDe(CONTACT_FILTERS, contact), retirer: () => setContact('') },
              marque && { cle: 'marque', libelle: 'Marque', valeur: marque, retirer: () => setMarque('') },
              nbPassages && { cle: 'passages', libelle: 'Passages', valeur: libDe(PASSAGES_FILTERS, nbPassages), retirer: () => setNbPassages('') },
            ].filter(Boolean);
            const effacer = () => { setEcheance(''); setContact(''); setMarque(''); setNbPassages(''); choisirVente(''); };
            return (
              <div className="page-toolbar ra-toolbar ra-fb">
                <div className="fb-barre">
                  <input className="page-search fb-recherche" placeholder="🔍 Immat, client, châssis…" defaultValue={q} onChange={onSearch} />
                  <Bascule
                    label="Périmètre"
                    valeur={montrerTout ? 'tout' : 'relancable'}
                    onChange={v => setMontrerTout(v === 'tout')}
                    options={[
                      { value: 'relancable', label: 'Relançables', title: 'Flottes, cessions, véhicules de service et échéances dépassées de plus de 18 mois masqués' },
                      { value: 'tout', label: 'Tout', title: 'Flottes et retards anciens compris' },
                    ]}
                  />
                  <BoutonPanneau
                    libelle={ventePreset ? `Vendu : ${PERIODES_VENTE.find(x => x.value === ventePreset).label.toLowerCase()}` : (venteDu || venteAu) ? 'Vendu : dates choisies' : 'Date de vente'}
                    icone={IconeCalendrier} largeur={340}>
                    <Rubrique titre="Vendu il y a">
                      <ChoixPastilles valeur={ventePreset} onChange={choisirVente} options={PERIODES_VENTE} />
                    </Rubrique>
                    <Rubrique titre="Ou des dates précises">
                      <div className="fb-duo">
                        <input type="date" aria-label="Vendu du" value={venteDu} onChange={e => { setVentePreset(''); setVenteDu(e.target.value); }} />
                        <input type="date" aria-label="Vendu au" value={venteAu} onChange={e => { setVentePreset(''); setVenteAu(e.target.value); }} />
                      </div>
                    </Rubrique>
                  </BoutonPanneau>
                  <BoutonPanneau libelle="Filtres" icone={IconeFiltres} compte={actifs.filter(f => f.cle !== 'vente').length} largeur={360}>
                    <Rubrique titre="Échéance"><ChoixPastilles valeur={echeance} onChange={setEcheance} options={ECHEANCE_FILTERS.filter(o => o.v).map(o => ({ value: o.v, label: o.label }))} /></Rubrique>
                    <Rubrique titre="Statut de contact"><ChoixPastilles valeur={contact} onChange={setContact} options={CONTACT_FILTERS.filter(o => o.v).map(o => ({ value: o.v, label: o.label }))} /></Rubrique>
                    <Rubrique titre="Nombre de passages"><ChoixPastilles valeur={nbPassages} onChange={setNbPassages} options={PASSAGES_FILTERS.filter(o => o.v).map(o => ({ value: o.v, label: o.label }))} /></Rubrique>
                    <Rubrique titre="Marque">
                      <select value={marque} onChange={e => setMarque(e.target.value)}>
                        <option value="">Toutes</option>
                        {marques.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Rubrique>
                    {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
                    <div className="fb-pied"><span>{nf.format(total)} véhicule(s)</span><button type="button" className="fb-lien" onClick={effacer}>Tout effacer</button></div>
                  </BoutonPanneau>
                  {canDelete && selected.size > 0 && (
                    <button className="ra-delete-btn" onClick={deleteSelected}>🗑️ Supprimer ({selected.size})</button>
                  )}
                </div>
                <Pastilles filtres={actifs} onToutEffacer={effacer}  />
              </div>
            );
          })()}

          {/* Bandeau de charge — compteurs cliquables (filtrent la liste) */}
          <div className="ra-stats">
            <button className={`ra-stat danger ${echeance === 'depassee' ? 'active' : ''}`} onClick={() => setEcheance(e => e === 'depassee' ? '' : 'depassee')}>
              <span className="ra-stat-num">{stats ? nf.format(stats.depassees) : '—'}</span>
              <span className="ra-stat-lbl">Dépassées</span>
            </button>
            <button className={`ra-stat warn ${echeance === '30j' ? 'active' : ''}`} onClick={() => setEcheance(e => e === '30j' ? '' : '30j')}>
              <span className="ra-stat-num">{stats ? nf.format(stats.j30) : '—'}</span>
              <span className="ra-stat-lbl">À échéance ≤ 30 j</span>
            </button>
            <button className={`ra-stat info ${echeance === '60j' ? 'active' : ''}`} onClick={() => setEcheance(e => e === '60j' ? '' : '60j')}>
              <span className="ra-stat-num">{stats ? nf.format(stats.j60) : '—'}</span>
              <span className="ra-stat-lbl">À échéance ≤ 60 j</span>
            </button>
          </div>

          {syncMsg && <div className="ra-info">✅ {syncMsg}</div>}
          {error && <div className="ra-error">⚠️ {error}</div>}

          <div className="ra-count">
            {loading ? 'Chargement…' : `${nf.format(total)} véhicule(s) relançable(s)`}
            {total > 0 && <span className="muted"> · {pageFrom}–{pageTo}</span>}
            {!montrerTout && <span className="muted"> · flottes &amp; retards &gt; 18 mois masqués</span>}
            <span className="ra-kbd-hint"><kbd>j</kbd>/<kbd>k</kbd> naviguer · <kbd>c</kbd> contacté · <kbd>↵</kbd> détail</span>
          </div>

          <div className="table-container">
            <table className="ra-table">
              <thead>
                <tr>
                  {canDelete && <th className="ra-check"></th>}
                  <SortableTh label="Véhicule" field="immatriculation" sort={sort} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Client" field="nom_client" sort={sort} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Code client" field="code_client" sort={sort} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Vendu le" field="date_vente_vn" sort={sort} dir={sortDir} onSort={handleSort} className="date" />
                  <SortableTh label="Dernier passage" field="dernier_passage" sort={sort} dir={sortDir} onSort={handleSort} className="date" />
                  <SortableTh label="Passages" field="nb_passages" sort={sort} dir={sortDir} onSort={handleSort} className="num" />
                  <SortableTh label="Échéance" field="prochaine_echeance" sort={sort} dir={sortDir} onSort={handleSort} className="date" />
                  {consentActif && <th className="num" title="Consentement à l'appel commercial (loi du 11 août 2026), brique A97 RGPD">Consent. tél.</th>}
                  <th>Statut</th>
                  <th title="Dernier statut atelier connu pour la plaque (Planning Atelier)">Atelier</th>
                </tr>
              </thead>
              <tbody>
                {vehicules.map((v, idx) => {
                  const badge = echeanceBadge(v.jours_restants, v.prochaine_echeance);
                  const echeanceTip = `Prochaine révision estimée le ${fmtDate(v.prochaine_echeance)} — déclenchée par ${v.echeance_source === 'km' ? 'le seuil kilométrique' : "le délai d'un an"} (km estimé ${fmtKm(v.km_estime_actuel)})`;
                  const rowCls = [detail?.vehicule?.chassis === v.chassis ? 'ra-active' : '', idx === focusIdx ? 'ra-focus' : ''].filter(Boolean).join(' ');
                  const auParking = parkingFound.has(normImmat(v.immatriculation));
                  return (
                    <tr key={v.chassis} className={rowCls}
                      ref={el => { if (el && idx === focusIdx) el.scrollIntoView({ block: 'nearest' }); }}
                      onMouseEnter={() => setFocusIdx(idx)}>
                      {canDelete && (
                        <td className="ra-check"><input type="checkbox" checked={selected.has(v.chassis)} onChange={() => toggleSelect(v.chassis)} /></td>
                      )}
                      <td onClick={() => openDetail(v.chassis, vehicules)} className="ra-cell-link">
                        <div className="ra-immat">
                          {(v.immatriculation || v.chassis || '').replace(/[-\s]/g, '')}
                          {auParking && <span className="ra-parking-badge" title="Identifié dans Gestion Parking">🅿️</span>}
                          {v.litige_id && <span className="ra-parking-badge" title="Un dossier existe pour ce véhicule dans l'onglet Litiges">⚖️</span>}
                        </div>
                        <div className="ra-sub">{v.marque || ''}</div>
                      </td>
                      <td onClick={() => openDetail(v.chassis, vehicules)} className="ra-cell-link">
                        <div className="ra-client-name" title={v.nom_client || ''}>{v.nom_client || '—'}</div>
                      </td>
                      <td className="ra-code-client">{v.code_client || ''}</td>
                      <td onClick={() => openDetail(v.chassis, vehicules)} className="ra-cell-link date">{fmtDate(v.date_vente_vn)}</td>
                      <td onClick={() => openDetail(v.chassis, vehicules)} className="ra-cell-link date">
                        <div>{fmtDate(v.dernier_passage)}</div>
                      </td>
                      <td className="num">{v.nb_passages ?? '—'}</td>
                      <td className="date"><span className={badge.cls} title={echeanceTip}>{badge.txt}</span></td>
                      {consentActif && <td className="num" onClick={() => openDetail(v.chassis, vehicules)}><ConsentBadge c={v.consentement} /></td>}
                      <td onClick={e => e.stopPropagation()}><StatutContact v={v} onChange={setRowStatut} /></td>
                      <td><AtelierStatut v={v} /></td>
                    </tr>
                  );
                })}
                {!loading && vehicules.length === 0 && (
                  <tr><td colSpan={(canDelete ? 10 : 9) + (consentActif ? 1 : 0)} className="ra-empty">
                    Aucun véhicule. {total === 0 && 'Lancez une synchronisation SFTP pour importer les passages.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="ra-pager">
              <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}>← Précédent</button>
              <span>{pageFrom}–{pageTo} / {nf.format(total)}</span>
              <button disabled={pageTo >= total} onClick={() => setOffset(o => o + PAGE_SIZE)}>Suivant →</button>
            </div>
          )}
        </>
      )}

      {tab === 'exclusions' && (
        <ExclusionsRelances user={user} privileged={privileged} peutModifier={peutModifier} />
      )}

      {tab === 'jamais' && (
        <>
          {(() => {
            const actifs = [
              (venteDu || venteAu) && { cle: 'vente', libelle: 'Vendu', valeur: ventePreset ? PERIODES_VENTE.find(x => x.value === ventePreset).label : `${fmtJourIso(venteDu)} → ${fmtJourIso(venteAu)}`, retirer: () => choisirVente('') },
              jrAnc && { cle: 'anc', libelle: 'Vendu', valeur: libDe(ANCIENNETE_FILTERS, jrAnc), retirer: () => setJrAnc('') },
              jrContact && { cle: 'contact', libelle: 'Contact', valeur: libDe(CONTACT_FILTERS, jrContact), retirer: () => setJrContact('') },
              marque && { cle: 'marque', libelle: 'Marque', valeur: marque, retirer: () => setMarque('') },
            ].filter(Boolean);
            const effacer = () => { setJrAnc(''); setJrContact(''); setMarque(''); choisirVente(''); };
            return (
              <div className="page-toolbar ra-toolbar ra-fb">
                <div className="fb-barre">
                  <input className="page-search fb-recherche" placeholder="🔍 Immat, client, châssis…" defaultValue={jrQ}
                    onChange={e => { const v = e.target.value; if (qTimer.current) clearTimeout(qTimer.current); qTimer.current = setTimeout(() => setJrQ(v), 350); }} />
                  <BoutonPanneau
                    libelle={ventePreset ? `Vendu : ${PERIODES_VENTE.find(x => x.value === ventePreset).label.toLowerCase()}` : (venteDu || venteAu) ? 'Vendu : dates choisies' : 'Date de vente'}
                    icone={IconeCalendrier} largeur={340}>
                    <Rubrique titre="Vendu il y a">
                      <ChoixPastilles valeur={ventePreset} onChange={choisirVente} options={PERIODES_VENTE} />
                    </Rubrique>
                    <Rubrique titre="Ou des dates précises">
                      <div className="fb-duo">
                        <input type="date" aria-label="Vendu du" value={venteDu} onChange={e => { setVentePreset(''); setVenteDu(e.target.value); }} />
                        <input type="date" aria-label="Vendu au" value={venteAu} onChange={e => { setVentePreset(''); setVenteAu(e.target.value); }} />
                      </div>
                    </Rubrique>
                  </BoutonPanneau>
                  <BoutonPanneau libelle="Filtres" icone={IconeFiltres} compte={actifs.filter(f => f.cle !== 'vente').length} largeur={360}>
                    <Rubrique titre="Statut de contact"><ChoixPastilles valeur={jrContact} onChange={setJrContact} options={CONTACT_FILTERS.filter(o => o.v).map(o => ({ value: o.v, label: o.label }))} /></Rubrique>
                    <Rubrique titre="Marque">
                      <select value={marque} onChange={e => setMarque(e.target.value)}>
                        <option value="">Toutes</option>
                        {marques.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Rubrique>
                    {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
                    <div className="fb-pied"><span>{nf.format(jr.total)} véhicule(s)</span><button type="button" className="fb-lien" onClick={effacer}>Tout effacer</button></div>
                  </BoutonPanneau>
                </div>
                <Pastilles filtres={actifs} onToutEffacer={effacer}  />
              </div>
            );
          })()}

          {error && <div className="ra-error">⚠️ {error}</div>}
          <div className="ra-count">
            {jrLoading ? 'Chargement…' : `${nf.format(jr.total)} véhicule(s) jamais venus en atelier`}
          </div>

          <div className="table-container">
            <table className="ra-table">
              <thead>
                <tr>
                  <SortableTh label="Véhicule" field="immatriculation" sort={jrSort} dir={jrSortDir} onSort={handleJrSort} />
                  <SortableTh label="Client" field="nom_client" sort={jrSort} dir={jrSortDir} onSort={handleJrSort} />
                  <SortableTh label="Code client" field="code_client" sort={jrSort} dir={jrSortDir} onSort={handleJrSort} />
                  <SortableTh label="Vendu le" field="date_vente_vn" sort={jrSort} dir={jrSortDir} onSort={handleJrSort} className="date" />
                  <th>Statut</th>
                  <th title="Dernier statut atelier connu pour la plaque (Planning Atelier)">Atelier</th>
                </tr>
              </thead>
              <tbody>
                {jr.vehicules.map(v => {
                  const badge = ancienneteBadge(v.jours_depuis_vente);
                  const auParking = parkingFound.has(normImmat(v.immatriculation));
                  return (
                    <tr key={v.chassis || v.id}>
                      <td onClick={() => openDetailJamais(v, jr.vehicules)} className="ra-cell-link">
                        <div className="ra-immat">
                          {(v.immatriculation || v.chassis || '').replace(/[-\s]/g, '') || '—'}
                          {auParking && <span className="ra-parking-badge" title="Identifié dans Gestion Parking">🅿️</span>}
                          {v.litige_id && <span className="ra-parking-badge" title="Un dossier existe pour ce véhicule dans l'onglet Litiges">⚖️</span>}
                        </div>
                        <div className="ra-sub">{v.marque || ''}</div>
                      </td>
                      <td onClick={() => openDetailJamais(v, jr.vehicules)} className="ra-cell-link">
                        <div className="ra-client-name" title={v.nom_client || ''}>{v.nom_client || '—'}</div>
                      </td>
                      <td className="ra-code-client">{v.code_client || ''}</td>
                      <td onClick={() => openDetailJamais(v, jr.vehicules)} className="ra-cell-link date">
                        <div>{fmtDate(v.date_vente_vn)}</div>
                        <span className={badge.cls}>{badge.txt}</span>
                      </td>
                      <td onClick={e => e.stopPropagation()}><StatutContact v={v} onChange={setRowStatut} /></td>
                      <td><AtelierStatut v={v} /></td>
                    </tr>
                  );
                })}
                {!jrLoading && jr.vehicules.length === 0 && (
                  <tr><td colSpan={6} className="ra-empty">Aucun véhicule. {jr.total === 0 && 'Lancez une synchronisation SFTP.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {jr.total > PAGE_SIZE && (
            <div className="ra-pager">
              <button disabled={jrOffset === 0} onClick={() => setJrOffset(o => Math.max(0, o - PAGE_SIZE))}>← Précédent</button>
              <span>{jrOffset + 1}–{Math.min(jrOffset + PAGE_SIZE, jr.total)} / {nf.format(jr.total)}</span>
              <button disabled={jrOffset + PAGE_SIZE >= jr.total} onClick={() => setJrOffset(o => o + PAGE_SIZE)}>Suivant →</button>
            </div>
          )}
        </>
      )}

      {/* Modale pop-up unifiée « fiche véhicule » (coordonnées, navigation, édition en direct, graphique km) */}
      {detail && (
        <VehiculeDetailModal
          key={(detail.vehicule?.chassis || detail.vehicule?.id || '') + '-' + detail.mode}
          detail={detail}
          detailLoading={detailLoading}
          navList={navVehiculesRef.current}
          onClose={() => setDetail(null)}
          onNav={handleNavDetail}
          onStatutChange={(statut) => saveContactField({ statut })}
          onCanalChange={(canal) => saveContactField({ canal })}
          onNoteSave={(note) => saveContactField({ note })}
          onOpenSms={(veh) => setSmsModal({ chassis: veh.chassis, nom: veh.nom_client, immat: veh.immatriculation, telephone: veh.tel1_mobile || veh.tel2_domicile, concession_id: veh.concession_id, concession_nom: veh.concession_nom })}
          consentSending={consentSending}
          consentMsg={consentMsg}
          onDemanderConsentement={async () => {
            const veh = detail?.vehicule;
            if (!veh?.chassis) return;
            if (!window.confirm('Envoyer au client, par SMS, le formulaire de consentement (appel + SMS commerciaux) via la brique A97 RGPD ?')) return;
            setConsentSending(true); setConsentMsg('');
            try {
              await relancesApi.demanderConsentement(veh.chassis, {});
              setConsentMsg('Demande envoyée par SMS. Le client peut répondre par le lien reçu ; la fiche se mettra à jour à sa réponse.');
              fetchContacter(veh.chassis); load();
            } catch (err) {
              setConsentMsg(err.response?.data?.error || 'Envoi impossible.');
            } finally { setConsentSending(false); }
          }}
        />
      )}

      {/* Modale SMS */}
      {modeleSmsOuvert && <ModeleSmsModal onClose={() => setModeleSmsOuvert(false)} />}
      {smsModal && <SmsModal target={smsModal} user={user} onClose={() => setSmsModal(null)} onSent={() => { load(); loadJr(); }} />}
    </div>
  );
}

export default RelancesAtelier;
